import React, { useState, useEffect, useCallback } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaGlobe,
  FaExternalLinkAlt,
} from "react-icons/fa";

// Map styles for a clean, professional look
const mapStyles = [
  {
    featureType: "all",
    elementType: "geometry.fill",
    stylers: [{ weight: "2.00" }],
  },
  {
    featureType: "all",
    elementType: "geometry.stroke",
    stylers: [{ color: "#9c9c9c" }],
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "all",
    stylers: [{ saturation: -100 }, { lightness: 45 }],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#eeeeee" }],
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ color: "#c8d7d4" }],
  },
];

// Map container style
const containerStyle = {
  width: "100%",
  height: "100%",
};

// Default center (US center)
const defaultCenter = {
  lat: 39.8283,
  lng: -98.5795,
};

// Custom marker icons for different facility types
const getMarkerIcon = (facilityType) => {
  const colors = {
    Landfill: "#ef4444",
    "Transfer Station": "#3b82f6",
    "Recycling Center": "#10b981",
    "Composting Facility": "#f59e0b",
    "Waste-to-Energy": "#8b5cf6",
    default: "#6b7280",
  };

  const color = colors[facilityType] || colors.default;
  const letter = facilityType ? facilityType.charAt(0).toUpperCase() : "F";

  return {
    url: `data:image/svg+xml;charset=UTF-8,%3csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='16' cy='16' r='14' fill='${encodeURIComponent(
      color
    )}' stroke='white' stroke-width='2'/%3e%3ctext x='16' y='21' text-anchor='middle' fill='white' font-size='12' font-weight='bold'%3e${letter}%3c/text%3e%3c/svg%3e`,
    scaledSize: { width: 32, height: 32 },
  };
};

const CollectorsMap = ({
  facilities = [],
  selectedFacility,
  onFacilitySelect,
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [resolvedFacilities, setResolvedFacilities] = useState([]);
  const [geocoding, setGeocoding] = useState(false);

  // Resolve facilities to ensure we have coordinates
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  useEffect(() => {
    let cancelled = false;

    async function resolveFacilitiesWithCoords() {
      if (!facilities || facilities.length === 0) {
        setResolvedFacilities([]);
        return;
      }

      setGeocoding(true);

      const resolved = await Promise.all(
        facilities.map(async (f) => {
          // Prefer coordinates if present
          const latFromCoords = f?.coordinates?.latitude;
          const lngFromCoords = f?.coordinates?.longitude;

          let lat =
            typeof latFromCoords === "number"
              ? latFromCoords
              : parseFloat(latFromCoords);
          let lng =
            typeof lngFromCoords === "number"
              ? lngFromCoords
              : parseFloat(lngFromCoords);

          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { ...f, _lat: lat, _lng: lng };
          }

          // Fallback to geocoding if we have an address and an API key
          if (
            GOOGLE_MAPS_API_KEY &&
            (f.address || f.city || f.state || f.zip)
          ) {
            try {
              const addressString =
                f.address || `${f.city || ""}, ${f.state || ""} ${f.zip || ""}`;
              const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                addressString
              )}&key=${GOOGLE_MAPS_API_KEY}`;
              const resp = await fetch(geocodeUrl);
              const data = await resp.json();
              if (
                data.status === "OK" &&
                data.results?.[0]?.geometry?.location
              ) {
                const { lat: gLat, lng: gLng } =
                  data.results[0].geometry.location;
                return { ...f, _lat: gLat, _lng: gLng };
              }
            } catch (e) {
              // Ignore geocode errors per item
            }
          }

          return null;
        })
      );

      const withCoords = resolved.filter(Boolean);
      if (!cancelled) {
        setResolvedFacilities(withCoords);
        setGeocoding(false);
      }
    }

    resolveFacilitiesWithCoords();
    return () => {
      cancelled = true;
    };
  }, [facilities, GOOGLE_MAPS_API_KEY]);

  // Calculate map center based on resolved facilities
  useEffect(() => {
    if (resolvedFacilities.length > 0) {
      const avgLat =
        resolvedFacilities.reduce((sum, f) => sum + parseFloat(f._lat), 0) /
        resolvedFacilities.length;
      const avgLng =
        resolvedFacilities.reduce((sum, f) => sum + parseFloat(f._lng), 0) /
        resolvedFacilities.length;
      setMapCenter({ lat: avgLat, lng: avgLng });
    }
  }, [resolvedFacilities]);

  // Handle marker click
  const handleMarkerClick = useCallback(
    (facility) => {
      setSelectedMarker(facility);
      if (onFacilitySelect) {
        onFacilitySelect(facility);
      }
    },
    [onFacilitySelect]
  );

  // Handle map load
  const onMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="h-96 bg-gray-100 rounded flex items-center justify-center">
        <div className="text-center p-6">
          <FaMapMarkerAlt className="text-4xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            Google Maps API Key Required
          </h3>
          <p className="text-gray-500 mb-4">
            To display the map, please configure your Google Maps API key.
          </p>
          <div className="bg-gray-50 p-4 rounded text-left text-sm">
            <p className="font-semibold mb-2">Setup Instructions:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-600">
              <li>
                Go to{" "}
                <a
                  href="https://console.cloud.google.com/google/maps-apis/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Google Cloud Console
                </a>
              </li>
              <li>Enable the Maps JavaScript API</li>
              <li>Create an API key</li>
              <li>
                Run:{" "}
                <code className="bg-gray-200 px-1 rounded">
                  node update-maps-key.js YOUR_API_KEY
                </code>
              </li>
              <li>Restart your development server</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  const validFacilities = resolvedFacilities;

  return (
    <div className="h-96 rounded overflow-hidden">
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} onLoad={onMapLoad}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={6}
          options={{
            styles: mapStyles,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true,
          }}
        >
          {/* Render facility markers */}
          {validFacilities.map((facility, index) => (
            <Marker
              key={`facility-${facility.id || index}`}
              position={{
                lat: parseFloat(facility._lat),
                lng: parseFloat(facility._lng),
              }}
              icon={getMarkerIcon(facility.facility_type)}
              onClick={() => handleMarkerClick(facility)}
              title={facility.name}
            />
          ))}

          {/* Info Window for selected marker */}
          {selectedMarker && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedMarker._lat),
                lng: parseFloat(selectedMarker._lng),
              }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="p-2 max-w-xs">
                <h3 className="font-bold text-lg mb-2">
                  {selectedMarker.name}
                </h3>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <span className="font-semibold mr-2">Type:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      {selectedMarker.facility_type}
                    </span>
                  </div>

                  <div>
                    <span className="font-semibold">Address:</span>
                    <p className="text-gray-600">
                      {selectedMarker.address}, {selectedMarker.city},{" "}
                      {selectedMarker.state} {selectedMarker.zip}
                    </p>
                  </div>

                  {selectedMarker.services && (
                    <div>
                      <span className="font-semibold">Services:</span>
                      <p className="text-gray-600">
                        {Array.isArray(selectedMarker.services)
                          ? selectedMarker.services.join(", ")
                          : selectedMarker.services}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedMarker.phone && (
                      <a
                        href={`tel:${selectedMarker.phone}`}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-xs"
                      >
                        <FaPhone />
                        <span>Call</span>
                      </a>
                    )}

                    {selectedMarker.website && (
                      <a
                        href={selectedMarker.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-xs"
                      >
                        <FaGlobe />
                        <span>Website</span>
                      </a>
                    )}

                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        `${selectedMarker.name} ${selectedMarker.address} ${selectedMarker.city} ${selectedMarker.state}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-xs"
                    >
                      <FaExternalLinkAlt />
                      <span>Directions</span>
                    </a>
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      {/* Map legend */}
      <div className="bg-white p-3 border-t">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span>Landfill</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span>Transfer Station</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span>Recycling Center</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
            <span>Composting</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full bg-purple-500"></div>
            <span>Waste-to-Energy</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectorsMap;
